// 2026-09-24 - FormuLearning - Step 6 결과 — T/R 요약·PK 곡선·PK Parameters·Mechanistic diagnostics·Weibull·내보내기 (이슈 #63)
// 파일 위치: frontend/src/features/step6/ResultSection.tsx
// 설계: 개정안 §1 REQ-FN-6-03·04 · §6. 원형: 국민대 PK 아티팩트 Reference vs Test 영역.

import { useEffect, useRef, useState } from 'react'
import type { SimulationResult } from '../../api/step6'
import { downloadExport, errorDetail } from '../../api/step6'
import { useUiStore } from '../../stores/uiStore'
import { chartTokens } from '../../lib/chartTheme'
import { Seg } from './fields'

interface Props {
  projectId: string
  result: SimulationResult
  outdated: boolean
}

const fx = (v: number | null | undefined, d = 2) => (v == null || !Number.isFinite(v) ? 'N/A' : v.toFixed(d))
const th = 'px-2 py-1 text-left text-[10px] font-medium text-text-secondary'
const td = 'px-2 py-1 text-[11px] text-text-body'

export default function ResultSection({ projectId, result, outdated }: Props) {
  const chartRef = useRef<HTMLDivElement>(null)
  const theme = useUiStore((s) => s.theme)
  const [scale, setScale] = useState<'lin' | 'log'>('lin')
  const [showFit, setShowFit] = useState(false)
  const [exportMsg, setExportMsg] = useState<string | null>(null)
  const { ref, test, ratio } = result
  const T = result.sim_time_h

  useEffect(() => {
    if (!chartRef.current) return
    let cancelled = false
    const t = chartTokens()
    const x = ref.curve.time_h
    const cr = ref.curve.conc_ng_mL
    const guide = { color: t.teal, width: 1, dash: 'dot' as const }
    import('plotly.js-dist-min').then((Plotly) => {
      if (cancelled || !chartRef.current) return
      Plotly.newPlot(chartRef.current, [
        { x, y: cr.map((v) => v * 1.05), type: 'scatter', mode: 'lines', line: guide, name: 'Reference ±5%', hoverinfo: 'skip' },
        { x, y: cr.map((v) => v * 0.95), type: 'scatter', mode: 'lines', line: guide, showlegend: false, hoverinfo: 'skip' },
        { x, y: cr, type: 'scatter', mode: 'lines', name: 'Reference', line: { color: t.teal, width: 2.5 } },
        { x: test.curve.time_h, y: test.curve.conc_ng_mL, type: 'scatter', mode: 'lines', name: 'Test', line: { color: t.purple, width: 2.5 } },
      ], {
        paper_bgcolor: 'transparent',
        plot_bgcolor: t.plotBg,
        font: { color: t.font, size: 10 },
        margin: { t: 16, r: 16, b: 40, l: 56 },
        xaxis: { title: { text: 'Time (h)' }, gridcolor: t.grid, range: [0, T] },
        yaxis: { title: { text: 'Plasma concentration (ng/mL)' }, gridcolor: t.grid, type: scale === 'log' ? 'log' : 'linear' },
        hovermode: 'x unified',
        legend: { orientation: 'h', y: 1.08, font: { size: 10 } },
      }, { displayModeBar: false, responsive: true })
    })
    return () => { cancelled = true }
  }, [ref, test, T, scale, theme])

  const savePng = async () => {
    if (!chartRef.current) return
    const Plotly = await import('plotly.js-dist-min')
    await Plotly.downloadImage(chartRef.current, { format: 'png', filename: 'step6_pk_curve', width: 1200, height: 520 })
  }
  const exportCsv = async (kind: 'simulation' | 'summary' | 'weibull') => {
    setExportMsg(kind === 'simulation' ? '전체 시계열을 다시 계산하는 중…' : null)
    try {
      await downloadExport(projectId, kind)
      setExportMsg(null)
    } catch (e) {
      setExportMsg(errorDetail(e))
    }
  }

  const cards = [
    { k: 'Cmax T/R', v: `${fx(ratio.cmax_pct, 1)}%`, r: `${fx(ref.metrics.cmax)} ng/mL`, t: `${fx(test.metrics.cmax)} ng/mL` },
    { k: `AUC₀–${T}h T/R`, v: `${fx(ratio.auc_pct, 1)}%`, r: `${fx(ref.metrics.auc, 0)} ng·h/mL`, t: `${fx(test.metrics.auc, 0)} ng·h/mL` },
    { k: 'Tmax', v: `${ratio.tmax_diff_h >= 0 ? '+' : ''}${fx(ratio.tmax_diff_h)} h`, r: `${fx(ref.metrics.tmax)} h`, t: `${fx(test.metrics.tmax)} h` },
  ]
  const extra = result.bio_extra
  const bioNote = result.bio_mode === 'ref'
    ? `BA 보정계수 k = F ÷ Fa(Reference) = ${fx(extra.k as number, 3)}${extra.clipped ? ' — 0~1 범위로 잘림(F 가 Reference 흡수분율보다 큼)' : ''}`
    : `예측 모드: CLh ${fx(extra.clh as number, 3)} · Fh ${fx(extra.fh as number, 3)} · Fg ${fx(extra.fg as number, 3)}${extra.fgBound ? ' (하한 0.7 적용)' : ''}`

  return (
    <div className="mb-4">
      <div className="mb-2 flex items-center gap-2">
        <h4 className="text-xs font-semibold uppercase tracking-wider text-text-secondary">Reference vs Test</h4>
        {outdated && <span className="text-[10px]" style={{ color: 'var(--color-warn)' }}>⚠ 입력이 바뀌었습니다 — 다시 실행하세요</span>}
        <span className="ml-auto text-[10px] text-text-muted">{new Date(result.run_at).toLocaleString()}</span>
      </div>
      <div className="mb-3 grid gap-2 sm:grid-cols-3">
        {cards.map((c) => (
          <div key={c.k} className="rounded border px-3 py-2" style={{ borderColor: 'var(--border-purple)', backgroundColor: 'var(--bg-chip-purple)' }}>
            <p className="text-[10px] text-text-secondary">{c.k}</p>
            <p className="text-base font-semibold text-accent-purple">{c.v}</p>
            <p className="text-[10px] text-text-secondary"><span style={{ color: 'var(--color-accent-teal)' }}>●</span> {c.r} · <span style={{ color: 'var(--color-accent-purple)' }}>●</span> {c.t}</p>
          </div>
        ))}
      </div>

      <div className="rounded border p-2" style={{ borderColor: 'var(--border-subtle)' }}>
        <div className="mb-1 flex items-center gap-2">
          <span className="text-[11px] font-medium text-text-body">PK curve (concentration vs time)</span>
          <span className="ml-auto"><Seg value={scale} onChange={setScale} options={[{ v: 'lin', label: 'Linear' }, { v: 'log', label: 'Log' }]} /></span>
        </div>
        <div ref={chartRef} style={{ width: '100%', height: 320 }} />
        <p className="mt-1 text-[10px] text-text-muted">
          점선은 Reference 곡선의 ±5% 가이드선입니다. 1인 기준 예측 곡선의 시각적 비교용이며, 생물학적동등성 판정(기하평균비 90% 신뢰구간 80.00–125.00%)을 대신하지 않습니다.
        </p>
      </div>

      <div className="mt-3 grid gap-3 lg:grid-cols-2">
        <div className="overflow-x-auto rounded border" style={{ borderColor: 'var(--border-subtle)' }}>
          <table className="w-full">
            <thead><tr><th className={th}>PK parameter</th><th className={th}>Unit</th><th className={th}>Reference</th><th className={th}>Test</th><th className={th}>T/R</th></tr></thead>
            <tbody>
              <tr><td className={td}>Cmax</td><td className={td}>ng/mL</td><td className={td}>{fx(ref.metrics.cmax)}</td><td className={td}>{fx(test.metrics.cmax)}</td><td className={td}>{fx(ratio.cmax_pct, 1)}%</td></tr>
              <tr><td className={td}>Tmax</td><td className={td}>h</td><td className={td}>{fx(ref.metrics.tmax)}</td><td className={td}>{fx(test.metrics.tmax)}</td><td className={td}>—</td></tr>
              <tr><td className={td}>AUC₀–{T}h</td><td className={td}>ng·h/mL</td><td className={td}>{fx(ref.metrics.auc, 1)}</td><td className={td}>{fx(test.metrics.auc, 1)}</td><td className={td}>{fx(ratio.auc_pct, 1)}%</td></tr>
              <tr><td className={td}>Fa (fraction absorbed)</td><td className={td}>—</td><td className={td}>{fx(ref.metrics.fa, 3)}</td><td className={td}>{fx(test.metrics.fa, 3)}</td><td className={td}>—</td></tr>
            </tbody>
          </table>
        </div>
        <div className="overflow-x-auto rounded border" style={{ borderColor: 'var(--border-subtle)' }}>
          <table className="w-full">
            <thead><tr><th className={th}>Mechanistic diagnostics</th><th className={th}>Upper intestine absorption (mg)</th><th className={th}>Lower intestine absorption (mg)</th><th className={th}>First-pass loss (mg)</th></tr></thead>
            <tbody>
              {([['Reference', ref], ['Test', test]] as const).map(([n, f]) => (
                <tr key={n}><td className={td}>{n}</td><td className={td}>{fx(f.metrics.upper, 3)}</td><td className={td}>{fx(f.metrics.lower, 3)}</td><td className={td}>{fx(f.metrics.fpl, 3)}</td></tr>
              ))}
            </tbody>
          </table>
          <p className="px-2 py-1 text-[10px] text-text-muted">{ref.pclass} permeability · {bioNote}</p>
        </div>
      </div>

      <button type="button" onClick={() => setShowFit((s) => !s)} className="mt-2 text-[10px] text-text-secondary hover:text-text-body">
        {showFit ? '▾' : '▸'} Weibull fit (용출 외삽·pH 보간 근거)
      </button>
      {showFit && (
        <div className="mt-1 overflow-x-auto rounded border" style={{ borderColor: 'var(--border-subtle)' }}>
          <table className="w-full">
            <thead><tr><th className={th}>Formulation</th><th className={th}>pH</th><th className={th}>F∞</th><th className={th}>α (h)</th><th className={th}>β</th><th className={th}>R²</th></tr></thead>
            <tbody>
              {([['Reference', ref], ['Test', test]] as const).flatMap(([n, f]) => f.fit.map((r) => (
                <tr key={`${n}-${r.pH}`}><td className={td}>{n}</td><td className={td}>{r.pH}</td><td className={td}>{fx(r.F_inf, 3)}</td><td className={td}>{fx(r.alpha_h, 4)}</td><td className={td}>{fx(r.beta, 3)}</td><td className={td}>{fx(r.R2, 4)}</td></tr>
              )))}
            </tbody>
          </table>
        </div>
      )}

      <div className="mt-3 flex flex-wrap items-center gap-2">
        <span className="text-[10px] text-text-secondary">Export</span>
        {([['simulation', 'Simulation CSV'], ['summary', 'PK summary CSV'], ['weibull', 'Weibull fit CSV']] as const).map(([k, label]) => (
          <button key={k} type="button" onClick={() => exportCsv(k)} className="rounded px-2 py-1 text-[10px] text-text-secondary hover:bg-bg-input"
            style={{ border: '1px solid var(--border-subtle)' }}>{label}</button>
        ))}
        <button type="button" onClick={savePng} className="rounded px-2 py-1 text-[10px] text-text-secondary hover:bg-bg-input"
          style={{ border: '1px solid var(--border-subtle)' }}>PK curve PNG</button>
        {exportMsg && <span className="text-[10px] text-text-muted">{exportMsg}</span>}
      </div>
    </div>
  )
}
